// Importar tipos necesarios
import type {
  OrderInterface,
  BusinessInterface,
  CashOperationInterface,
  EconomicCycle,
  SelledReport,
  PriceInvoiceInterface,
} from '@/interfaces/ServerInterfaces'

declare global {
  interface Navigator {
    serial: {
      getPorts(): Promise<unknown[]>
      requestPort(_options?: {
        filters?: { usbVendorId: number }[]
      }): Promise<unknown>
    }
  }
}

export interface PrinterSetupResult {
  success: boolean
  port?: unknown
  error?: string
}

export interface PrinterSettings {
  paperWidth: 58 | 80
  port?: unknown
}

export type PaperWidth = 58 | 80

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class PrinterUtils {
  private static readonly STORAGE_KEY = 'tecopos_printer_settings'

  static isWebSerialSupported(): boolean {
    return 'serial' in navigator
  }

  static getPrinterSettings(): PrinterSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (stored) {
        const settings = JSON.parse(stored) as PrinterSettings
        // Validar que paperWidth sea válido
        if (settings.paperWidth === 58 || settings.paperWidth === 80) {
          return settings
        }
      }
    } catch {
      // Error parsing, usar valores por defecto
    }

    // Valores por defecto
    return {
      paperWidth: 58, // Por defecto 58mm como mencionaste
    }
  }

  static savePrinterSettings(settings: PrinterSettings): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings))
    } catch {
      // Error guardando, continuar sin guardar
    }
  }

  static getPaperWidth(): PaperWidth {
    return this.getPrinterSettings().paperWidth
  }

  static setPaperWidth(width: PaperWidth): void {
    const currentSettings = this.getPrinterSettings()
    this.savePrinterSettings({
      ...currentSettings,
      paperWidth: width,
    })
  }

  static getBrowserSupport(): {
    supported: boolean
    reason?: string
    recommendations: string[]
    browserInfo: string
  } {
    // Detectar el navegador
    const userAgent = navigator.userAgent
    const isChrome =
      userAgent.includes('Chrome') &&
      !userAgent.includes('Edg') &&
      !userAgent.includes('Firefox')
    const isEdge = userAgent.includes('Edg')
    const isFirefox = userAgent.includes('Firefox')
    const isSafari =
      userAgent.includes('Safari') && !userAgent.includes('Chrome')
    const isOpera = userAgent.includes('OPR')

    let browserName = 'Navegador desconocido'
    if (isFirefox) browserName = 'Mozilla Firefox'
    else if (isEdge) browserName = 'Microsoft Edge'
    else if (isChrome) browserName = 'Google Chrome'
    else if (isSafari) browserName = 'Safari'
    else if (isOpera) browserName = 'Opera'

    if (!('serial' in navigator)) {
      // Mensajes específicos según el navegador
      let specificRecommendations: string[] = []

      if (isFirefox) {
        specificRecommendations = [
          'Mozilla Firefox NO soporta Web Serial API nativamente',
          'SOLUCIÓN: Use Google Chrome o Microsoft Edge en su lugar',
          'Alternativa: Instale la extensión "WebSerial for Firefox" (requiere app nativa)',
          'Recomendado: Cambie a Chrome o Edge para mejor compatibilidad',
        ]
      } else if (isSafari) {
        specificRecommendations = [
          'Safari NO soporta Web Serial API',
          'SOLUCIÓN: Use Google Chrome o Microsoft Edge',
          'Safari no tiene planes de implementar esta característica',
        ]
      } else {
        specificRecommendations = [
          'Use Google Chrome versión 89 o superior',
          'Use Microsoft Edge versión 89 o superior',
          'Si está usando Edge, vaya a edge://flags/#enable-experimental-web-platform-features',
          'Si está usando Chrome, vaya a chrome://flags/#enable-experimental-web-platform-features',
          'Reinicie el navegador después de cambiar las configuraciones',
        ]
      }

      return {
        supported: false,
        reason: isFirefox
          ? 'Mozilla Firefox no soporta Web Serial API'
          : isSafari
            ? 'Safari no soporta Web Serial API'
            : 'Web Serial API no está disponible',
        browserInfo: `Navegador detectado: ${browserName}`,
        recommendations: specificRecommendations,
      }
    }

    // Verificar si está en contexto seguro (HTTPS o localhost)
    if (
      location.protocol !== 'https:' &&
      !location.hostname.includes('localhost') &&
      !location.hostname.includes('127.0.0.1')
    ) {
      return {
        supported: false,
        reason: 'Web Serial API requiere contexto seguro (HTTPS)',
        browserInfo: `Navegador: ${browserName}, Protocolo: ${location.protocol}`,
        recommendations: [
          'Use HTTPS en lugar de HTTP',
          'O use localhost para desarrollo',
          'O use 127.0.0.1 para desarrollo local',
        ],
      }
    }

    // Verificaciones específicas para Edge
    if (isEdge) {
      return {
        supported: true,
        browserInfo: `Microsoft Edge detectado - ${userAgent.match(/Edg\/[\d.]+/)?.[0] || 'versión desconocida'}`,
        recommendations: [
          'Edge está configurado correctamente',
          'Si tiene problemas, verifique edge://flags/#enable-experimental-web-platform-features',
          'Asegúrese de que la impresora USB esté conectada antes de configurar',
        ],
      }
    }

    return {
      supported: true,
      browserInfo: `${browserName} detectado`,
      recommendations: ['Todo está configurado correctamente'],
    }
  }

  static async setupPrinter(): Promise<PrinterSetupResult> {
    try {
      const support = this.getBrowserSupport()
      if (!support.supported) {
        return {
          success: false,
          error: `${support.reason}. ${support.browserInfo}. Recomendaciones: ${support.recommendations.join('. ')}`,
        }
      }

      // Verificar puertos existentes primero
      const existingPorts = await navigator.serial.getPorts()
      if (existingPorts.length > 0) {
        return {
          success: true,
          port: existingPorts[0],
        }
      }

      // Solicitar nuevo puerto - debe ser en respuesta a interacción del usuario
      try {
        const port = await navigator.serial.requestPort({
          filters: [
            // Impresoras térmicas y chipsets comunes USB-Serial
            { usbVendorId: 0x04b8 }, // Epson
            { usbVendorId: 0x0519 }, // Star Micronics
            { usbVendorId: 0x20d1 }, // Generic thermal printers
            { usbVendorId: 0x067b }, // Prolific Technology, Inc. (PL2303)
            { usbVendorId: 0x0403 }, // FTDI (FT232/FT2232)
            { usbVendorId: 0x1a86 }, // QinHeng/WCH (CH340/CH341)
            { usbVendorId: 0x10c4 }, // Silicon Labs (CP210x)
            { usbVendorId: 0x0483 }, // STMicroelectronics (CDC ACM)
          ],
        })

        return {
          success: true,
          port,
        }
      } catch (requestError: unknown) {
        // Reintentar sin filtros por compatibilidad (el usuario verá todos los puertos)
        try {
          const unfiltPort = await navigator.serial.requestPort()
          return {
            success: true,
            port: unfiltPort,
          }
        } catch {
          let errorMessage = 'Error desconocido'
          const error = requestError as { name?: string; message?: string }

          if (error.name === 'NotAllowedError') {
            errorMessage =
              'Permiso denegado. El usuario canceló la selección de puerto o denegó el acceso.'
          } else if (error.message?.includes('user gesture')) {
            errorMessage =
              'Esta función debe ejecutarse en respuesta directa a un clic del usuario.'
          } else if (error.name === 'NotFoundError') {
            errorMessage =
              'No se encontraron dispositivos serie compatibles. Verifique que la impresora esté conectada.'
          }

          return {
            success: false,
            error: errorMessage,
          }
        }
      }
    } catch (_error: unknown) {
      const errorObj = _error as { message?: string }
      return {
        success: false,
        error: `Error general: ${errorObj.message || 'Error desconocido'}`,
      }
    }
  }

  static async setupPrinterWithRetry(
    maxRetries = 3
  ): Promise<PrinterSetupResult> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.setupPrinter()
        if (result.success) {
          return result
        }

        // Si es el último intento, devolver el error
        if (attempt === maxRetries) {
          return {
            success: false,
            error: `${result.error} (Intento ${attempt}/${maxRetries})`,
          }
        }

        // Esperar un poco antes del siguiente intento
        await new Promise((resolve) => setTimeout(resolve, 1000))
      } catch (error: unknown) {
        const errorObj = error as { message?: string }
        if (attempt === maxRetries) {
          return {
            success: false,
            error: `Error en intento ${attempt}/${maxRetries}: ${errorObj.message || 'Error desconocido'}`,
          }
        }
      }
    }

    return {
      success: false,
      error: 'Error después de múltiples intentos',
    }
  }

  static async printData(
    port: unknown,
    data: Uint8Array
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const serialPort = port as {
        readable?: boolean
        writable?: {
          getWriter(): {
            write(_data: Uint8Array): Promise<void>
            releaseLock(): void
          }
        }
        open(_options: {
          baudRate: number
          dataBits: number
          stopBits: number
          parity: string
        }): Promise<void>
        close(): Promise<void>
      }

      // Abrir puerto si no está abierto
      if (!serialPort.readable || !serialPort.writable) {
        try {
          await serialPort.open({
            baudRate: 9600,
            dataBits: 8,
            stopBits: 1,
            parity: 'none',
          })
        } catch (openError: unknown) {
          const error = openError as { name?: string; message?: string }
          if (error.name === 'InvalidStateError') {
            return {
              success: false,
              error:
                'Puerto ya está abierto o en uso. Intente desconectar y reconectar la impresora.',
            }
          } else if (error.message?.includes('Access denied')) {
            return {
              success: false,
              error:
                'Acceso denegado al puerto USB. Verifique permisos del navegador y del sistema operativo.',
            }
          }
          throw openError
        }
      }

      const writer = serialPort.writable?.getWriter()
      if (!writer) {
        return {
          success: false,
          error: 'No se pudo obtener el writer del puerto',
        }
      }

      try {
        await writer.write(data)
        writer.releaseLock()
        return { success: true }
      } catch (writeError: unknown) {
        writer.releaseLock()
        const error = writeError as { name?: string; message?: string }

        if (error.name === 'NetworkError') {
          return {
            success: false,
            error:
              'Error de conexión con la impresora. Verifique que esté conectada y encendida.',
          }
        } else if (error.message?.includes('Access denied')) {
          return {
            success: false,
            error:
              'Acceso denegado durante la escritura. Verifique permisos del navegador.',
          }
        }
        throw writeError
      }
    } catch (error: unknown) {
      const errorObj = error as { message?: string; name?: string }

      // Manejo específico de errores comunes
      if (errorObj.name === 'InvalidStateError') {
        return {
          success: false,
          error: 'Estado inválido del puerto. Intente reconectar la impresora.',
        }
      } else if (errorObj.message?.includes('Access denied')) {
        return {
          success: false,
          error:
            'Acceso denegado al dispositivo USB. Verifique permisos del navegador y del sistema.',
        }
      } else if (errorObj.message?.includes('Failed to execute')) {
        return {
          success: false,
          error:
            'Error de ejecución en el dispositivo USB. Verifique que la impresora esté funcionando correctamente.',
        }
      }

      return {
        success: false,
        error: `Error imprimiendo: ${errorObj.message || 'Error desconocido'}`,
      }
    }
  }

  static async printDataWithRetry(
    port: unknown,
    data: Uint8Array,
    maxRetries = 2
  ): Promise<{ success: boolean; error?: string }> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const result = await this.printData(port, data)

      if (result.success) {
        return result
      }

      // Si es el último intento, devolver el error
      if (attempt === maxRetries) {
        return {
          success: false,
          error: `${result.error} (Intento ${attempt}/${maxRetries})`,
        }
      }

      // Esperar un poco antes del siguiente intento
      await new Promise((resolve) => setTimeout(resolve, 500))
    }

    return {
      success: false,
      error: 'Error después de múltiples intentos de impresión',
    }
  }

  static async checkPrinterStatus(): Promise<{
    connected: boolean
    ports: number
    details: string[]
  }> {
    try {
      if (!this.isWebSerialSupported()) {
        return {
          connected: false,
          ports: 0,
          details: ['Web Serial API no soportada'],
        }
      }

      const ports = await navigator.serial.getPorts()
      const details = ports.map((port: unknown, index: number) => {
        const serialPort = port as { getInfo?(): unknown }
        return `Puerto ${index + 1}: ${
          serialPort.getInfo
            ? JSON.stringify(serialPort.getInfo())
            : 'Información no disponible'
        }`
      })

      return {
        connected: ports.length > 0,
        ports: ports.length,
        details: details.length > 0 ? details : ['No hay puertos configurados'],
      }
    } catch (error: unknown) {
      const errorObj = error as { message?: string }
      return {
        connected: false,
        ports: 0,
        details: [
          `Error verificando estado: ${errorObj.message || 'Error desconocido'}`,
        ],
      }
    }
  }

  static getPrinterSetupGuide(): string[] {
    const userAgent = navigator.userAgent
    const isEdge = userAgent.includes('Edg')
    const isFirefox = userAgent.includes('Firefox')

    const baseSteps = [
      '1. Conecte la impresora USB al puerto de la computadora',
      '2. Encienda la impresora y espere a que esté lista',
      '3. Verifique que está usando un navegador compatible (Chrome 89+ o Edge 89+)',
    ]

    let browserSpecificSteps: string[] = []

    if (isEdge) {
      browserSpecificSteps = [
        '4. IMPORTANTE para Edge: Vaya a edge://flags/#enable-experimental-web-platform-features',
        '5. Habilite "Experimental Web Platform features" y reinicie Edge',
        '6. Asegúrese de estar en HTTPS o localhost (http://localhost:3000 está bien)',
      ]
    } else if (isFirefox) {
      browserSpecificSteps = [
        '4. ❌ FIREFOX NO SOPORTA Web Serial API nativamente',
        '5. SOLUCIÓN: Cambie a Google Chrome o Microsoft Edge',
        '6. Alternativa: Instale extensión "WebSerial for Firefox" + app nativa',
        '7. Recomendado: Use Chrome o Edge para evitar complicaciones',
      ]
    } else {
      browserSpecificSteps = [
        '4. IMPORTANTE para Chrome: Vaya a chrome://flags/#enable-experimental-web-platform-features',
        '5. Habilite "Experimental Web Platform features" y reinicie Chrome',
        '6. Asegúrese de estar en HTTPS o localhost (http://localhost:3000 está bien)',
      ]
    }

    const finalSteps = isFirefox
      ? ['8. Una vez en Chrome/Edge, regrese aquí y configure la impresora']
      : [
          '7. Haga clic en "Configurar Impresora" (debe ser un clic directo del usuario)',
          '8. Seleccione su impresora USB en el diálogo que aparece',
          '9. Otorgue permisos cuando el navegador se lo solicite',
          '10. La impresora quedará configurada para futuras impresiones automáticas',
        ]

    return [...baseSteps, ...browserSpecificSteps, ...finalSteps]
  }

  static async printTicketWithDialog(
    order: unknown,
    business: unknown,
    paperWidth: PaperWidth
  ): Promise<void> {
    const { PrintTicket } = await import('./PrintTicket')
    return PrintTicket({
      order: order as OrderInterface | null | undefined,
      business: business as BusinessInterface,
      rollSize: paperWidth,
    })
  }

  static async printOrderTicketWithDialog(
    order: unknown,
    business: unknown,
    paperWidth: PaperWidth
  ): Promise<void> {
    const { PrintOrderTicket } = await import(
      '../containers/store/orders/PrintOrderTicket'
    )
    return PrintOrderTicket({
      order: order as OrderInterface | null | undefined,
      business: business as BusinessInterface | null,
      rollSize: paperWidth,
    })
  }

  static async printCashOperationTicketWithDialog(
    cashOperation: unknown,
    ecoCycle: unknown,
    paperWidth: PaperWidth
  ): Promise<void> {
    const { PrintCashOperationTicket } = await import(
      '../components/economicCycle/PrintCashOperationTicket'
    )
    return PrintCashOperationTicket({
      cashOperation: cashOperation as CashOperationInterface[],
      ecoCycle: ecoCycle as EconomicCycle | null,
      rollSize: paperWidth,
    })
  }

  static async printSelledReportTicketWithDialog(
    selledReport: unknown,
    totales: unknown,
    ecoCycle: unknown,
    paperWidth: PaperWidth
  ): Promise<void> {
    const { PrintSelledReportTicket } = await import(
      '../containers/economicCycles/PrintSelledReportTicket'
    )
    return PrintSelledReportTicket({
      selledReport: selledReport as SelledReport | null,
      totales: totales as {
        quantity: number
        totalCost: PriceInvoiceInterface
        totalSales: PriceInvoiceInterface[]
      },
      ecoCycle: ecoCycle as EconomicCycle | null,
      rollSize: paperWidth,
    })
  }

  static getEdgeTroubleshootingSteps(): string[] {
    return [
      'Pasos para solucionar problemas en Microsoft Edge:',
      '',
      '1. Verifique la versión de Edge (debe ser 89 o superior):',
      '   - Vaya a edge://settings/help',
      '   - Verifique que la versión sea 89.0.904.0 o superior',
      '',
      '2. Habilite Web Serial API en Edge:',
      '   - Abra edge://flags/#enable-experimental-web-platform-features',
      '   - Cambie el estado a "Enabled"',
      '   - Reinicie completamente Edge',
      '',
      '3. Verifique permisos de sitio:',
      '   - Vaya a edge://settings/content/serialPorts',
      '   - Asegúrese de que el sitio tenga permisos',
      '',
      '4. Problemas comunes:',
      '   - Error "user gesture": Debe hacer clic directamente en configurar impresora',
      '   - Error "NotFound": Impresora no conectada o no detectada',
      '   - Error "NotAllowed": Permisos denegados por el usuario',
      '',
      '5. Si nada funciona:',
      '   - Desconecte y reconecte la impresora USB',
      '   - Reinicie Edge completamente',
      '   - Intente en modo incógnito para descartar extensiones',
    ]
  }

  static async printWithUSBOnly(
    data: Uint8Array,
    onSuccess?: () => void,
    onError?: (_error: string) => void
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Verificar soporte del navegador
      const support = this.getBrowserSupport()
      if (!support.supported) {
        const errorMessage =
          `${support.reason}. ${support.browserInfo}. ` +
          `Recomendaciones: ${support.recommendations.join('. ')}`
        onError?.(errorMessage)
        return { success: false, error: errorMessage }
      }

      // Verificar si hay puertos configurados
      const status = await this.checkPrinterStatus()
      if (!status.connected) {
        const errorMessage =
          'No hay impresora USB configurada. Configure la impresora primero.'
        onError?.(errorMessage)
        return { success: false, error: errorMessage }
      }

      // Usar puertos existentes
      const existingPorts = await navigator.serial.getPorts()
      if (existingPorts.length === 0) {
        const errorMessage =
          'No hay puertos USB disponibles. Reconecte la impresora y configure nuevamente.'
        onError?.(errorMessage)
        return { success: false, error: errorMessage }
      }

      // Intentar imprimir con cada puerto disponible
      for (const port of existingPorts) {
        const printResult = await this.printDataWithRetry(port, data)
        if (printResult.success) {
          onSuccess?.()
          return { success: true }
        }
      }

      // Si ningún puerto funcionó
      const errorMessage =
        'No se pudo imprimir en ningún puerto USB disponible. Verifique que la impresora esté conectada y funcionando.'
      onError?.(errorMessage)
      return { success: false, error: errorMessage }
    } catch (_error: unknown) {
      const errorObj = _error as { message?: string }
      const errorMessage = `Error en impresión USB: ${errorObj.message || 'Error desconocido'}`
      onError?.(errorMessage)
      return { success: false, error: errorMessage }
    }
  }

  static async printWithFallback(
    data: Uint8Array,
    onSuccess?: () => void,
    onError?: (_error: string) => void
  ): Promise<{
    success: boolean
    method: 'usb' | 'browser' | 'none'
    error?: string
  }> {
    try {
      // Intentar impresión USB primero
      const status = await this.checkPrinterStatus()
      if (status.connected) {
        const existingPorts = await navigator.serial.getPorts()
        if (existingPorts.length > 0) {
          const printResult = await this.printDataWithRetry(
            existingPorts[0],
            data
          )
          if (printResult.success) {
            onSuccess?.()
            return { success: true, method: 'usb' }
          }
        }
      }

      // Si falla USB, intentar impresión del navegador como respaldo
      return await this.printWithBrowserFallback(data, onSuccess, onError)
    } catch (_error: unknown) {
      const errorObj = _error as { message?: string }
      const errorMessage = `Error en impresión USB: ${errorObj.message || 'Error desconocido'}`
      onError?.(errorMessage)

      // Intentar impresión del navegador como respaldo
      return await this.printWithBrowserFallback(data, onSuccess, onError)
    }
  }

  static async printWithBrowserFallback(
    data: Uint8Array,
    onSuccess?: () => void,
    onError?: (_error: string) => void
  ): Promise<{
    success: boolean
    method: 'usb' | 'browser' | 'none'
    error?: string
  }> {
    try {
      // Convertir datos a texto para impresión del navegador
      const textData = new TextDecoder('utf-8').decode(data)

      // Crear ventana de impresión
      const printWindow = window.open('', '_blank', 'width=400,height=600')
      if (!printWindow) {
        const errorMessage =
          'No se pudo abrir ventana de impresión. Verifique que el navegador permita ventanas emergentes.'
        onError?.(errorMessage)
        return { success: false, method: 'none', error: errorMessage }
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Ticket de Impresión</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 10px;
                white-space: pre-wrap;
                line-height: 1.2;
              }
              @media print {
                body { margin: 0; padding: 5px; }
              }
            </style>
          </head>
          <body>
            ${textData}
          </body>
        </html>
      `)

      printWindow.document.close()

      // Imprimir automáticamente
      setTimeout(() => {
        printWindow.print()
        setTimeout(() => {
          printWindow.close()
          onSuccess?.()
        }, 1000)
      }, 500)

      return { success: true, method: 'browser' }
    } catch (_error: unknown) {
      const errorObj = _error as { message?: string }
      const errorMessage = `Error en impresión del navegador: ${errorObj.message || 'Error desconocido'}`
      onError?.(errorMessage)
      return { success: false, method: 'none', error: errorMessage }
    }
  }

  static getClientTroubleshootingGuide(): string[] {
    return [
      'Guía de solución de problemas para clientes:',
      '',
      'PROBLEMA: "Must be handling a user gesture"',
      'SOLUCIÓN:',
      '  - Haga clic DIRECTAMENTE en el botón de imprimir',
      '  - No use atajos de teclado, debe ser un clic del mouse',
      '  - No haga clic en otros elementos antes de imprimir',
      '',
      'PROBLEMA: "Access denied" o "Acceso denegado"',
      'SOLUCIÓN:',
      '  - Verifique que la impresora esté conectada y encendida',
      '  - Desconecte y reconecte el cable USB',
      '  - Reinicie el navegador completamente',
      '  - Verifique permisos del navegador en configuración',
      '',
      'PROBLEMA: "Failed to execute open on USBDevice"',
      'SOLUCIÓN:',
      '  - La impresora puede estar siendo usada por otra aplicación',
      '  - Cierre todas las aplicaciones que puedan usar la impresora',
      '  - Reinicie la computadora si el problema persiste',
      '',
      'CONFIGURACIÓN DEL NAVEGADOR:',
      '  - Use Chrome 89+ o Edge 89+',
      '  - Habilite "Experimental Web Platform features" en flags',
      '  - Asegúrese de estar en HTTPS o localhost',
      '  - Otorgue permisos cuando el navegador los solicite',
      '',
      'SI NADA FUNCIONA:',
      '  - Use la impresión del navegador como alternativa',
      '  - Configure la impresora como impresora predeterminada del sistema',
      '  - Use una aplicación de impresión externa',
    ]
  }
}
